import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaSync } from 'react-icons/fa';
import styles from './AmazonSubmissionsPage.module.css';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const AUTH_TOKEN_KEY = 'authToken';

// API Service
const fetchSubmissions = async (token) => {
  const response = await fetch(`${API_BASE_URL}/oauth_amazon/amazon/submissions`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch submissions: ${response.status}`);
  }

  return response.json();
};

// Utility functions
const getStatusIcon = (status) => {
  const statusMap = {
    'done': <FaCheckCircle className={styles.statusIconSuccess} />,
    'completed': <FaCheckCircle className={styles.statusIconSuccess} />,
    'success': <FaCheckCircle className={styles.statusIconSuccess} />,
    'error': <FaTimesCircle className={styles.statusIconError} />,
    'failed': <FaTimesCircle className={styles.statusIconError} />,
    'processing': <FaSpinner className={styles.statusIconProcessing} />,
    'in_progress': <FaSpinner className={styles.statusIconProcessing} />
  };
  return statusMap[status?.toLowerCase()] || <FaExclamationTriangle className={styles.statusIconWarning} />;
};

const getStatusText = (status) => {
  const textMap = {
    'done': 'Completed',
    'completed': 'Completed',
    'success': 'Completed',
    'error': 'Failed',
    'failed': 'Failed',
    'processing': 'Processing',
    'in_progress': 'Processing'
  };
  return textMap[status?.toLowerCase()] || status || 'Unknown';
};

const formatErrorText = (error, issues) => {
  if (issues?.length) {
    return issues.map(issue => 
      `[${issue.severity || 'INFO'}] ${issue.code || 'N/A'}: ${issue.message || 'No message'}${issue.categories ? ` (${issue.categories.join(', ')})` : ''}`
    ).join('\n\n');
  }
  return error || 'No issues';
};

// Components
const StatusCell = ({ status }) => (
  <div className={styles.statusContainer}>
    {getStatusIcon(status)}
    <span className={styles.statusText}>{getStatusText(status)}</span>
  </div>
);

const ErrorCell = ({ error, issues }) => {
  const hasIssues = (issues && issues.length > 0) || error;
  const displayText = formatErrorText(error, issues);
  
  return (
    <div className={`${styles.errorContainer} ${!hasIssues ? styles.noIssuesContainer : ''}`}>
      <span className={`${styles.errorText} ${!hasIssues ? styles.noIssuesText : ''}`}>
        {displayText || 'No issues'}
      </span>
    </div>
  );
};

const ImageCell = ({ imageUrl, asin }) => (
  <div className={styles.imageContainer}>
    {imageUrl ? (
      <img 
        src={imageUrl} 
        alt={`Product image for ASIN ${asin}`}
        className={styles.productImage}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
    ) : null}
    <div className={styles.noImagePlaceholder} style={{ display: imageUrl ? 'none' : 'flex' }}>
      <span>No Image</span>
    </div>
  </div>
);

const SubmissionStatusCell = ({ submissionId, error, timestamp }) => {
  const wasSubmitted = submissionId && submissionId !== 'N/A';
  
  const getStatusDisplay = () => {
    if (wasSubmitted) {
      return 'Submitted';
    }
    return 'Failed to Submit';
  };

  const getStatusClass = () => {
    if (wasSubmitted) {
      return styles.statusCompleted;
    }
    return styles.statusFailed;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Parse the UTC timestamp
      const utcDate = new Date(timestamp);
      
      // IST is UTC +5:30 (5 hours 30 minutes = 330 minutes)
      const istOffsetMinutes = 330;
      const istDate = new Date(utcDate.getTime() + (istOffsetMinutes * 60 * 1000));
      
      const dateStr = istDate.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
      const timeStr = istDate.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      return `${dateStr}\n${timeStr}`;
    } catch (error) {
      return '';
    }
  };

  return (
    <div className={styles.statusContainer}>
      <div className={styles.statusContent}>
        <span className={`${styles.statusText} ${getStatusClass()}`}>
          {getStatusDisplay()}
        </span>
        {timestamp && (
          <span className={styles.timestampText}>
            {formatTimestamp(timestamp)}
          </span>
        )}
      </div>
    </div>
  );
};

const ActionCell = ({ error, issues }) => {
  const hasIssues = (issues && issues.length > 0) || error;
  
  if (!hasIssues) {
    return <div className={styles.actionContainer}></div>;
  }
  
  return (
    <div className={styles.actionContainer}>
      <span className={styles.actionText}>
        Raise a ticket with amazon at{' '}
        <a 
          href="https://sellercentral.amazon.in/help/center?redirectSource=HelpHub" 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.actionLink}
        >
          sellercentral.amazon.in
        </a>
      </span>
    </div>
  );
};

const SubmissionRow = ({ submission, index }) => (
  <tr key={submission.submission_id || index} className={styles.tableRow}>
    <td className={`${styles.tableCell} ${styles.imageCell}`}>
      <ImageCell imageUrl={submission.main_product_image_locator} asin={submission.asin} />
    </td>
    <td className={`${styles.tableCell} ${styles.asinCell}`}>{submission.asin || 'N/A'}</td>
    <td className={`${styles.tableCell} ${styles.skuCell}`}>{submission.sku || 'N/A'}</td>
    <td className={styles.tableCell}>
      <span className={styles.submissionId}>{submission.submission_id || 'N/A'}</span>
    </td>
    <td className={styles.tableCell}>
      <ErrorCell error={submission.error} issues={submission.issues} />
    </td>
    <td className={styles.tableCell}>
      <SubmissionStatusCell submissionId={submission.submission_id} error={submission.error} timestamp={submission.updated_at} />
    </td>
    <td className={`${styles.tableCell} ${styles.actionCell}`}>
      <ActionCell error={submission.error} issues={submission.issues} />
    </td>
  </tr>
);

// Layout Components
const HeaderControls = ({ onRefresh, refreshing }) => (
  <button className={styles.refreshButton} onClick={onRefresh} disabled={refreshing}>
    <FaSync className={refreshing ? styles.spinning : ''} />
    {refreshing ? 'Refreshing...' : 'Refresh'}
  </button>
);

const LoadingState = () => (
  <div className={styles.loadingContainer}>
    <FaSpinner className={styles.spinning} />
    <p>Loading your Amazon submissions...</p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className={styles.container}>
    <h1 className={styles.title}>Amazon Submissions</h1>
    <div className={styles.errorMessage}>
      <strong>Error:</strong> {error}
      <button onClick={onRetry} className={styles.retryButton}>
        Try Again
      </button>
    </div>
  </div>
);

const EmptyState = () => (
  <div className={styles.emptyState}>
    <h3>No submissions found</h3>
    <p>You haven't made any Amazon submissions yet.</p>
  </div>
);

const SubmissionsTable = ({ submissions, tableRows }) => (
  <div className={styles.tableContainer}>
    <table className={styles.table}>
      <thead>
        <tr className={styles.tableHeader}>
          <th className={styles.tableHeaderCell}>Image</th>
          <th className={styles.tableHeaderCell}>ASIN</th>
          <th className={styles.tableHeaderCell}>SKU</th>
          <th className={styles.tableHeaderCell}>Submission ID</th>
          <th className={styles.tableHeaderCell}>Error/Issues</th>
          <th className={styles.tableHeaderCell}>Submission Status</th>
          <th className={styles.tableHeaderCell}>Action</th>
        </tr>
      </thead>
      <tbody>{tableRows}</tbody>
    </table>
  </div>
);

const AmazonSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAmazonSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      const data = await fetchSubmissions(token);
      setSubmissions(data.submissions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchAmazonSubmissions();
    setRefreshing(false);
  }, [fetchAmazonSubmissions]);

  const handleRetry = useCallback(() => {
    fetchAmazonSubmissions();
  }, [fetchAmazonSubmissions]);

  // Memoized values
  const hasSubmissions = useMemo(() => submissions.length > 0, [submissions.length]);
  const tableRows = useMemo(() => 
    submissions.map((submission, index) => 
      <SubmissionRow key={submission.submission_id || index} submission={submission} index={index} />
    ), [submissions]);

  useEffect(() => {
    fetchAmazonSubmissions();
  }, [fetchAmazonSubmissions]);

  // Render states
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={handleRetry} />;

  return (
    <div className={styles.container}>
      <div className={styles.headerWithRefresh}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Amazon Submissions</h1>
          <p className={styles.subtitle}>Track your Amazon listing submissions and their status</p>
        </div>
        <HeaderControls 
          onRefresh={handleRefresh} 
          refreshing={refreshing} 
        />
      </div>
      
      {hasSubmissions ? (
        <SubmissionsTable submissions={submissions} tableRows={tableRows} />
      ) : (
        <EmptyState />
      )}
    </div>
  );
};

export default AmazonSubmissionsPage;
